import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './RACContact.css';

const RACContact = ({ onRequestCallback, onScheduleMeeting }) => {
  console.log('[RACContact] Component rendered');
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    city: '',
    investmentRange: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const investmentRanges = [
    '₹2,50,000 - ₹5,00,000',
    '₹5,00,000 - ₹10,00,000',
    '₹10,00,000+',
    'Not sure yet'
  ];
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      await addDoc(collection(db, 'rac_inquiries'), {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim().toLowerCase(),
        city: formData.city.trim(),
        investmentRange: formData.investmentRange,
        message: formData.message.trim() || '',
        status: 'new',
        created_at: serverTimestamp(),
        source: 'rac_program_page'
      });
      
      // Trigger notification
      try {
        await addDoc(collection(db, 'notifications'), {
          type: 'rac_inquiry',
          title: 'New RAC Program Inquiry',
          message: `${formData.name} from ${formData.city} is interested in RAC Program`,
          data: {
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            city: formData.city,
            investmentRange: formData.investmentRange
          },
          status: 'unread',
          created_at: serverTimestamp(),
          target_roles: ['admin', 'adminassistant', 'operationhead']
        });
      } catch (notifError) {
        console.warn('Notification creation failed (non-critical):', notifError);
      }
      
      setFormData({
        name: '',
        phone: '',
        email: '',
        city: '',
        investmentRange: '',
        message: ''
      });
      setSuccess(true);
      
    } catch (err) {
      console.error('Error submitting RAC inquiry:', err);
      setError('Failed to submit. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <section className="rac-contact">
      <div className="rac-contact-container">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rac-section-title"
        >
          Contact Section
        </motion.h2>
        <div className="rac-gold-line"></div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="rac-contact-content"
        >
          {success ? (
            <div className="rac-contact-success">
              <div className="rac-success-icon">✓</div>
              <h3>Thank you for your interest!</h3>
              <p>We'll contact you shortly to discuss the RAC Program.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="rac-contact-form">
              {error && (
                <div className="rac-contact-error">{error}</div>
              )}
              
              <div className="rac-form-row">
                <div className="rac-form-group">
                  <label htmlFor="name">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rac-input"
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="rac-form-group">
                  <label htmlFor="phone">Phone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="rac-input"
                    placeholder="10-digit mobile number"
                    pattern="[0-9]{10}"
                    maxLength="10"
                  />
                </div>
              </div>
              
              <div className="rac-form-row">
                <div className="rac-form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rac-input"
                    placeholder="your.email@example.com"
                  />
                </div>
                
                <div className="rac-form-group">
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="rac-input"
                    placeholder="Your city"
                  />
                </div>
              </div>
              
              <div className="rac-form-group">
                <label htmlFor="investmentRange">Asset Purchase Amount Range *</label>
                <select
                  id="investmentRange"
                  name="investmentRange"
                  value={formData.investmentRange}
                  onChange={handleChange}
                  required
                  className="rac-input"
                >
                  <option value="">Select asset purchase amount range</option>
                  {investmentRanges.map(range => (
                    <option key={range} value={range}>{range}</option>
                  ))}
                </select>
              </div>
              
              <div className="rac-form-group">
                <label htmlFor="message">Message (Optional)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="rac-input"
                  placeholder="Any questions or specific requirements..."
                  rows="4"
                />
              </div>
              
              <div className="rac-contact-buttons">
                <button
                  type="submit"
                  className="rac-btn rac-btn-gold"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Request Call Back'}
                </button>
                <button
                  type="button"
                  onClick={onScheduleMeeting}
                  className="rac-btn rac-btn-gold"
                >
                  Schedule a Meeting
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default RACContact;


